import {
  APP_INITIALIZER,
  ApplicationConfig,
  importProvidersFrom,
  provideZoneChangeDetection,
} from '@angular/core';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
  withInterceptors,
} from '@angular/common/http';
import {
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import { EffectsModule } from '@ngrx/effects';
import { IgniteModule } from '@ignite/angular';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { StoreModule } from '@ngrx/store';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideRouter, RouterModule } from '@angular/router';

import { ApiInterceptor } from './shared/common/api.interceptor';
import { MockInterceptor } from './infrastructure/http/interceptors/mock.interceptor';
import { authInterceptor } from './infrastructure/http/interceptors/auth.interceptor';
import { httpErrorInterceptor } from './infrastructure/http/interceptors/http-error.interceptor';
import { AppEffects } from './store/app.effects';
import { appReducers } from './store/app.reducers';
import { routes } from './app.routes';
import { appInit } from './app-init';
import { AuthService } from './core/services/auth.service';
import { FeatureFlagsService } from './core/services/feature-flags.service';

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(
    http,
    `${window.envData.assets}i18n/`,
    '.json'
  );
}

/**
 * Factory function to initialize feature flags during app bootstrap.
 * Consolidated initialization - no separate config loader needed.
 * Wraps in async to handle both sync and async errors uniformly.
 */
export function initializeFeatureFlags(
  service: FeatureFlagsService
): () => Promise<void> {
  return async () => {
    await service.initialize();
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: appInit,
      deps: [TranslateService, AuthService, FeatureFlagsService],
      multi: true,
    },
    // Initialize feature flags (LaunchDarkly or fallback to defaults)
    {
      provide: APP_INITIALIZER,
      useFactory: initializeFeatureFlags,
      deps: [FeatureFlagsService],
      multi: true,
    },
    { provide: HTTP_INTERCEPTORS, useClass: MockInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ApiInterceptor, multi: true },
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideHttpClient(
      withInterceptorsFromDi(),
      withInterceptors([authInterceptor, httpErrorInterceptor])
    ),
    { provide: Window, useValue: window },
    importProvidersFrom(
      StoreModule.forRoot(appReducers),
      EffectsModule.forRoot([...AppEffects]),
      StoreDevtoolsModule.instrument({ maxAge: 25, name: 'Platform Dev' }),
      IgniteModule.forRoot(),
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
        defaultLanguage: 'en-US',
      }),
      RouterModule.forRoot(routes) // Import RouterModule with routes
    ),
  ],
};
